/**
 * 記事本文HTML内の画像スロット（プレースホルダー）を管理するユーティリティ
 *
 * プレースホルダー形式（HTMLコメント）:
 *   <!-- IMAGE:slot-name -->
 *
 * 例:
 *   <!-- IMAGE:coolors-top -->
 *   <!-- IMAGE:coolors-camera -->
 */

export interface ImageSlotEntry {
  slotName: string;
  url: string;
  alt: string;
}

/**
 * HTML文字列からスロット名の一覧を抽出する
 * 重複は除去して返す
 */
export const parseImageSlots = (html: string): string[] => {
  const regex = /<!--\s*IMAGE:([a-zA-Z0-9_-]+)\s*-->/g;
  const found = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    found.add(match[1]);
  }
  return Array.from(found);
};

/**
 * HTML文字列内のプレースホルダーを実際の <figure> / <img> タグへ置換する
 *
 * @param html       元のHTML文字列
 * @param imageMap   { slotName -> ImageSlotEntry } のマップ
 * @returns          置換後のHTML文字列
 */
export const replaceImageSlots = (
  html: string,
  imageMap: Record<string, ImageSlotEntry>,
): string => {
  return html.replace(
    /<!--\s*IMAGE:([a-zA-Z0-9_-]+)\s*-->/g,
    (_match, slotName: string) => {
      const entry = imageMap[slotName];
      if (!entry) {
        // 画像未設定のスロットはそのまま残す（プレビュー時は視覚的なプレースホルダーを表示）
        return `<div class="image-slot-placeholder" data-slot="${slotName}">
  <span class="image-slot-label">📷 IMAGE:${slotName}（画像未設定）</span>
</div>`;
      }
      return `<figure class="article-figure">
  <img src="${entry.url}" alt="${entry.alt || slotName}" />
</figure>`;
    },
  );
};

/**
 * ファイル名から拡張子を除いたスロット名を生成する
 * 例: "coolors-top.png" → "coolors-top"
 */
export const fileNameToSlotName = (fileName: string): string =>
  fileName.replace(/\.[^.]+$/, '');
